// AI Generated: FAQ Section component with accordion - Figma node-id=2033-1008
import React, { useState, useCallback } from 'react';
import styles from '$styles/ind/registration/faqSection.module.scss';

interface IFAQ {
  id: number;
  question: string;
  answer: string | string[];
}

interface IFAQSectionProps {
  isMobile?: boolean;
  isDesktop?: boolean;
  customFaqs?: IFAQ[];
  pageType?: 'registration' | 'learn';
}

// Default FAQs for registration page
const registrationFaqs: IFAQ[] = [
  {
    id: 1,
    question: 'What are the benefits of being a redBus partner?',
    answer:
      'After bus registration on redBus, seats are guaranteed to be booked more easily than ever. redBus provides great business insights to its partners. For all the bus operations, redBus partners will get on time payment. The commission is also extremely transparent and there is no hidden cost involved. Every redBus partner gets personalized support. Bus companies partnering with redBus can maximize their revenue by being a part of redBus exclusive programs.',
  },
  {
    id: 2,
    question: 'What is the process of bus registration on redBus?',
    answer: [
      'redBus bus operator registration is extremely simple',
      '1) Submit an application with relevant documents.',
      '2) The Business Development team will reach out to you.',
      '3) Internal departments will verify the documents.',
      '4) An agreement will be drawn.',
      '5) Once the agreement is accepted, redBus partners can get their inventory live on redBus',
    ],
  },
  {
    id: 3,
    question: 'Is there a partnership fee?',
    answer:
      'redBus charges minimum commission on each ticket sold via redBus website or mobile application.',
  },
  {
    id: 4,
    question: 'How can I manage my inventory on redBus?',
    answer:
      'You can do so by partnering with software vendors or can also use our free software (BOGDS) to manage your inventory.',
  },
  {
    id: 5,
    question: 'How do I get paid as a redBus partner?',
    answer:
      'You can do so by partnering with software vendors or can also use our free software (BOGDS) to manage your inventory.',
  },
  {
    id: 6,
    question: 'How does redBus settle payments?',
    answer:
      'redBus is known for its on-time payment among its partners. redBus settles payment via online transfer to partner bank accounts as per the payment frequency.',
  },
  {
    id: 7,
    question: 'How can I know if a seat is booked?',
    answer:
      'redBus uses software to manage the inventory, which also helps the bus operators to track their bookings. After bus registration on redBus, the same software will help the bus operators to know the updates on seat booking.',
  },
];

// FAQs for learn/newcomer page - matching Figma design node-id=2143:6753
const learnFaqs: IFAQ[] = [
  {
    id: 1,
    question: 'The market is so competitive, will I get success?',
    answer:
      'The demand is still outpacing supply and with 1000s of untapped routes, there is still a lot of potential for new operators.',
  },
  {
    id: 2,
    question: "I'm new to this industry, will someone from redBus guide me through the process?",
    answer:
      'redBus will provide a dedicated person to manage your account and provide support.',
  },
  {
    id: 3,
    question: 'What are the benefits of being a redBus partner?',
    answer:
      'After bus registration on redBus, seats are guaranteed to be booked more easily than ever. redBus provides great business insights to its partners. For all the bus operations, redBus partners will get on time payment. The commission is also extremely transparent and there is no hidden cost involved. Every redBus partner gets personalized support. Bus companies partnering with redBus can maximize their revenue by being a part of redBus exclusive programs.',
  },
  {
    id: 4,
    question: 'How can I manage my inventory on redBus?',
    answer:
      'You can do so by partnering with software vendors or can also use our free software (BOGDS) to manage your inventory.',
  },
  {
    id: 5,
    question: 'How do I get paid as a redBus partner?',
    answer:
      'You can do so by partnering with software vendors or can also use our free software (BOGDS) to manage your inventory.',
  },
  {
    id: 6,
    question: 'How can I manage my inventory on redBus?',
    answer:
      'redBus is known for its on-time payment among its partners. redBus settles payment via online transfer to partner bank accounts as per the payment frequency.',
  },
  {
    id: 7,
    question: 'How can I know if a seat is booked?',
    answer:
      'redBus uses software to manage the inventory, which also helps the bus operators to track their bookings. After bus registration on redBus, the same software will help the bus operators to know the updates on seat booking.',
  },
];

const FAQSection: React.FC<IFAQSectionProps> = ({ customFaqs, pageType = 'registration' }) => {
  const [openFaqId, setOpenFaqId] = useState<number | null>(1);
  
  // Use custom FAQs if provided, otherwise use page-specific FAQs
  const faqs = customFaqs || (pageType === 'learn' ? learnFaqs : registrationFaqs);

  const toggleFaq = useCallback((id: number) => {
    setOpenFaqId((prev) => (prev === id ? null : id));
  }, []);

  const renderAnswer = (answer: string | string[]) => {
    if (Array.isArray(answer)) {
      return (
        <div className={styles.faqAnswerList}>
          {answer.map((line, index) => (
            <p key={index}>{line}</p>
          ))}
        </div>
      );
    }
    return <p>{answer}</p>;
  };

  return (
    <section className={styles.faqSection} id="faq">
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.sectionTitle}>Frequently asked questions</h2>
          <p className={styles.sectionSubtitle}>
            Maximize your reach with your inventory live on most of the major platforms
          </p>
        </div>

        {/* FAQ Items */}
        <div className={styles.faqList}>
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className={`${styles.faqItem} ${openFaqId === faq.id ? styles.open : ''}`}
            >
              <button
                className={styles.faqQuestion}
                onClick={() => toggleFaq(faq.id)}
                aria-expanded={openFaqId === faq.id}
              >
                <span>{faq.question}</span>
                <span className={styles.faqIcon}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={openFaqId === faq.id ? styles.rotated : ''}
                  >
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="#1d1d1d"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>
              <div className={styles.faqAnswer}>
                {renderAnswer(faq.answer)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
// AI Generated: End of FAQ section - Figma node-id=2033-1008
